import type { TenantSuperadmin } from '@t4s/api-client/model'
import { useId } from 'react'
import { Kicker, ModalDialog } from './modal-dialog.tsx'

/**
 * The confirmation before a link is minted — docs/ux/PLATFORM-ADMIN.md §11.3 and §19.
 *
 * It is confirmed because it **revokes that person's outstanding link if they have one**,
 * and because whoever holds the new link can take over that account. The dialog says both,
 * in those words: this is the only action on this surface that reaches inside a tenant, and
 * it exists because there is no email in this system.
 *
 * Modal, Escape refused, focus returned — the shell (`ModalDialog`) does all three, as for
 * Retire and Rotate. **A failed mint stays here** (`fix-modals-close-on-success`, which until
 * this dialog only Retire and Rotate followed): the sentence is shown inside, the button is
 * usable again, Cancel closes it and clears the sentence. There is no typed field, so nothing
 * is lost either way.
 */
export function MintLinkDialog({
  user,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  readonly user: TenantSuperadmin
  readonly pending: boolean
  /** A failed confirm's sentence, shown inside the dialog. */
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onConfirm: () => void
}) {
  const headingId = useId()
  const reset = user.status === 'active'

  return (
    <ModalDialog labelledBy={headingId}>
      <Kicker lead={reset ? 'Takes over an administrator account' : 'Issues a new invite'} />
      <h2 id={headingId}>
        {reset ? 'Mint a password-reset link' : 'Mint a new invite'} for {user.fullName}?
      </h2>
      <p>
        {reset
          ? 'Whoever holds this link can set this account’s password and sign in as them. This is the platform owner taking over a tenant’s administrator account, and it is the price of there being no email in this system.'
          : 'Whoever holds this link activates this account and chooses its password.'}
      </p>
      {user.link !== null && user.link.state === 'outstanding' ? (
        <p>
          <strong>This revokes their outstanding link.</strong> Anybody already holding it will find
          it no longer works.
        </p>
      ) : null}

      {error === undefined ? null : (
        <p className="notice notice-failure" role="alert">
          {error}
        </p>
      )}

      <div className="confirm-controls">
        <button type="button" className="button" onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button
          type="button"
          className="button button-danger"
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? 'Minting…' : 'Mint the link'}
        </button>
      </div>
    </ModalDialog>
  )
}
